import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { BackupButton } from '@/components/BackupButton';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Trophy, FileText, Wallet, LogOut, Menu, X, ChevronRight, User as UserIcon, AlertCircle, Settings, BarChart2, TrendingUp, Pencil, Eye, EyeOff, KeyRound } from 'lucide-react';
import { ROUTE_PATHS, UserTier, UserStatus, formatCurrency } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useBetting } from '@/hooks/useBetting';
import { useLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { IMAGES } from '@/assets/images';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, isLoading: authLoading, logout, isSuspended, effectiveStatus, refreshUser, patchUser } = useAuth();
  const { users: allUsers, updateUser, fightSettings } = useUserManagement();
  const { bets: allBets, matches: allMatches } = useBetting();
  const { t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // ── Nickname edit dialog state ───────────────────────────────────────────────
  const [showNicknameDialog, setShowNicknameDialog] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');
  const [nicknameSaving, setNicknameSaving] = useState(false);

  // ── Change Password dialog state (player only) ─────────────────────────────
  const [showChangePwDialog, setShowChangePwDialog] = useState(false);
  const [changePwCurrent, setChangePwCurrent] = useState('');
  const [changePwNew, setChangePwNew] = useState('');
  const [changePwConfirm, setChangePwConfirm] = useState('');
  const [changePwShowCurrent, setChangePwShowCurrent] = useState(false);
  const [changePwShowNew, setChangePwShowNew] = useState(false);
  const [changePwShowConfirm, setChangePwShowConfirm] = useState(false);
  const [changePwSaving, setChangePwSaving] = useState(false);
  const [changePwError, setChangePwError] = useState('');
  const [changePwSuccess, setChangePwSuccess] = useState(false);
  // ── First-time login popup state ─────────────────────────────────────────────
  // Shown automatically when user has no nickname set (first login signal).
  // Dismissed permanently once a nickname is saved.
  const [showFirstLoginPopup, setShowFirstLoginPopup] = useState(false);
  const [firstLoginNickname, setFirstLoginNickname] = useState('');
  const [firstLoginOldPw, setFirstLoginOldPw] = useState('');
  const [firstLoginNewPw, setFirstLoginNewPw] = useState('');
  const [firstLoginConfirmPw, setFirstLoginConfirmPw] = useState('');
  const [firstLoginShowOldPw, setFirstLoginShowOldPw] = useState(false);
  const [firstLoginShowNewPw, setFirstLoginShowNewPw] = useState(false);
  const [firstLoginShowConfirmPw, setFirstLoginShowConfirmPw] = useState(false);
  const [firstLoginSaving, setFirstLoginSaving] = useState(false);
  const [firstLoginError, setFirstLoginError] = useState('');

  // Show first-time setup screen when firstLogin flag is true
  useEffect(() => {
    if (authLoading) return;
    if (user && user.firstLogin === true) {
      // Pre-fill nickname if one was already set during user creation
      setFirstLoginNickname(user.nickname && user.nickname !== 'null' ? user.nickname : '');
      setFirstLoginOldPw('');
      setFirstLoginNewPw('');
      setFirstLoginConfirmPw('');
      setFirstLoginError('');
      setShowFirstLoginPopup(true);
    } else {
      setShowFirstLoginPopup(false);
    }
  }, [authLoading, user?.id, user?.firstLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePassword = useCallback(async () => {
    if (!user) return;
    if (!changePwCurrent) { setChangePwError('Please enter your current password.'); return; }
    if (!changePwNew || changePwNew.length < 4) { setChangePwError('New password must be at least 4 characters.'); return; }
    if (changePwNew !== changePwConfirm) { setChangePwError('New password and confirmation do not match.'); return; }
    setChangePwSaving(true);
    setChangePwError('');
    try {
      await updateUser(user.id, { password: changePwNew });
      setChangePwSuccess(true);
      setTimeout(() => {
        setShowChangePwDialog(false);
        setChangePwCurrent('');
        setChangePwNew('');
        setChangePwConfirm('');
        setChangePwSuccess(false);
      }, 1500);
    } catch (err) {
      setChangePwError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setChangePwSaving(false);
    }
  }, [user, changePwCurrent, changePwNew, changePwConfirm, updateUser]);

  const handleFirstLoginSave = useCallback(async () => {
    if (!user) return;
    if (!firstLoginNickname.trim()) { setFirstLoginError('Please enter a nickname.'); return; }
    if (!firstLoginOldPw) { setFirstLoginError('Please enter your current password.'); return; }
    if (!firstLoginNewPw || firstLoginNewPw.length < 4) { setFirstLoginError('New password must be at least 4 characters.'); return; }
    if (firstLoginNewPw !== firstLoginConfirmPw) { setFirstLoginError('New password and Re-Enter New Password do not match.'); return; }
    setFirstLoginSaving(true);
    setFirstLoginError('');
    try {
      // Save nickname + new password + clear first_login flag
      await updateUser(user.id, { nickname: firstLoginNickname.trim(), password: firstLoginNewPw, firstLogin: false });
      patchUser({ nickname: firstLoginNickname.trim(), firstLogin: false });
      setShowFirstLoginPopup(false);
    } catch (err) {
      setFirstLoginError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setFirstLoginSaving(false);
    }
  }, [user, firstLoginNickname, firstLoginOldPw, firstLoginNewPw, firstLoginConfirmPw, updateUser, patchUser]);

  const handleSaveNickname = useCallback(async () => {
    if (!user || !nicknameInput.trim()) return;
    setNicknameSaving(true);
    try {
      await updateUser(user.id, { nickname: nicknameInput.trim() });
      patchUser({ nickname: nicknameInput.trim() });
      setShowNicknameDialog(false);
      setNicknameInput('');
    } finally {
      setNicknameSaving(false);
    }
  }, [user, nicknameInput, updateUser, patchUser]);

  // Background poll every 10s as a fallback safety net
  // Immediate updates are done via patchUser() in MatchBetting and Reports
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => { refreshUser(); }, 10000);
    return () => clearInterval(interval);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Balance of credit limit = user's own credit limit minus sum of credit limits allocated to direct children
  const balanceOfCreditLimit = useMemo(() => {
    if (!user) return 0;
    const allocatedToChildren = allUsers
      .filter(u => u.parentId === user.id)
      .reduce((sum, u) => sum + (u.creditLimit || 0), 0);
    return (user.creditLimit || 0) - allocatedToChildren;
  }, [user, allUsers]);

  // Used credit = credit limit minus available credit limit
  const usedCredit = (user?.creditLimit || 0) - ((user?.availableCreditLimit ?? user?.creditLimit) || 0);

  // ── Sidebar Balance: directly mirrors WalletBalance.tsx Grand Total ──────────
  // Uses identical Book3 formulas + fightPctSnapshot per-bet, so the number
  // always equals the Grand Total > Company > Total shown in the Report table.
  const reportBalance = useMemo(() => {
    if (!user) return 0;

    // ── Book5 row type & helpers (exact mirror of WalletBalance.tsx) ───────────
    interface _BR {
      origBP: number; bpT: number; commT: number; wlT: number; totalT: number;
      bpF: number; commF: number; wlF: number; totalF: number;
      bpC: number; commC: number; wlC: number; totalC: number;
    }
    const _e = (): _BR => ({ origBP:0,bpT:0,commT:0,wlT:0,totalT:0,bpF:0,commF:0,wlF:0,totalF:0,bpC:0,commC:0,wlC:0,totalC:0 });
    const _a = (a: _BR, b: _BR): _BR => ({
      origBP:a.origBP+b.origBP, bpT:a.bpT+b.bpT, commT:a.commT+b.commT,
      wlT:a.wlT+b.wlT, totalT:a.totalT+b.totalT,
      bpF:a.bpF+b.bpF, commF:a.commF+b.commF, wlF:a.wlF+b.wlF, totalF:a.totalF+b.totalF,
      bpC:a.bpC+b.bpC, commC:a.commC+b.commC, wlC:a.wlC+b.wlC, totalC:a.totalC+b.totalC,
    });
    // leafRow — mirrors WalletBalance.tsx leafRow (Book5)
    const _leaf = (bp: number, wl: number, childTP: number, parentTP: number, fp: number): _BR => {
      const origBP = bp, bpT = bp;
      const commT  = bpT * (1 - childTP);
      const wlT    = wl;
      const totalT = (wlT + commT) - bpT;
      const bpF    = bpT * fp;
      const commF  = bpF * (1 - parentTP) * -1;  // Book5: new commF
      const wlF    = wlT * fp * -1;
      const totalF = bpF + commF + wlF;
      const bpC    = origBP - bpF;
      const commC  = bpC * (1 - parentTP) * -1;
      const wlC    = (wlT + wlF) * -1;
      const totalC = bpC + commC + wlC;
      return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
    };
    const _agentSum = (s: _BR): _BR => ({
      ...s, totalT:(s.wlT+s.commT)-s.bpT, totalF:s.bpF+s.commF+s.wlF, totalC:s.bpC+s.commC+s.wlC,
    });
    // agentAsChildRow — mirrors WalletBalance.tsx agentAsChildRow (Book5)
    const _asChild = (c: _BR, childTP: number, parentTP: number, fp: number): _BR => {
      const origBP = c.origBP, bpT = c.bpC;
      const commT  = bpT * (1 - childTP);
      const wlT    = c.wlC * -1;
      const totalT = (wlT + commT) - bpT;
      const bpF    = bpT * fp;
      const commF  = bpF * (1 - parentTP) * -1;  // Book5: new commF
      const wlF    = wlT * fp * -1;
      const totalF = bpF + commF + wlF;
      const bpC    = origBP - bpF;
      const commC  = bpC * (1 - parentTP) * -1;
      const wlC    = (wlT + wlF) * -1;
      const totalC = bpC + commC + wlC;
      return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
    };

    // ── Build betsByUser: PLAYER bets on COMPLETED matches (WON or LOST) ───────
    // Matches WalletBalance filteredBets logic (no date filter in sidebar)
    const betsByUser = new Map<string, typeof allBets>();
    allBets.forEach(bet => {
      const betUser = allUsers.find(u => u.id === bet.userId);
      if (betUser?.tier !== UserTier.PLAYER) return;
      const match = allMatches.find(m => m.id === bet.matchId);
      if (!match || match.status !== 'COMPLETED') return;
      if (bet.status !== 'WON' && bet.status !== 'LOST') return;
      const arr = betsByUser.get(bet.userId) || [];
      arr.push(bet);
      betsByUser.set(bet.userId, arr);
    });

    // allUsersForTree: ticketPrice stored ×100 (mirrors WalletBalance.tsx allUsersForTree)
    const usersForTree = allUsers.map(u => ({
      ...u,
      ticketPrice: (u.ticketPrice ?? 1) * 100, // ×100, formulas divide by 100
    }));

    // groupPlayerBetsBySnapshot — mirrors WalletBalance.tsx
    const groupBySnap = (
      bets: typeof allBets,
      fallbackFP: number,
      playerTP: number,  // decimal
      parentTP: number,  // decimal
    ): _BR => {
      const groupMap = new Map<number, typeof allBets>();
      for (const bet of bets) {
        const snap = ((bet as any).fightPctSnapshot !== undefined && (bet as any).fightPctSnapshot !== null)
          ? (bet as any).fightPctSnapshot as number
          : fallbackFP;
        const list = groupMap.get(snap) || [];
        list.push(bet);
        groupMap.set(snap, list);
      }
      let combined = _e();
      for (const [snapInt, groupBets] of groupMap.entries()) {
        const fp = snapInt / 100;
        for (const bet of groupBets) {
          const isWin = bet.status === 'WON';
          const wl = isWin ? bet.amount * (bet.oddsAtTime || 1) : 0;
          combined = _a(combined, _leaf(bet.amount, wl, playerTP, parentTP, fp));
        }
      }
      return combined;
    };

    // computeNode — mirrors WalletBalance.tsx computeNode, returns ownSummary
    const computeNode = (nodeId: string, parentTPx100: number, fightPctInt: number): _BR | null => {
      const nodeUser = usersForTree.find(u => u.id === nodeId);
      if (!nodeUser) return null;

      if (nodeUser.tier === UserTier.PLAYER) {
        const bets = betsByUser.get(nodeId);
        if (!bets || bets.length === 0) return null;
        const playerTP = nodeUser.ticketPrice / 100;  // ×100 stored, /100 to decimal
        const pTP      = parentTPx100 / 100;
        return groupBySnap(bets, fightPctInt, playerTP, pTP);
      }

      // Agent: recurse children
      const children = usersForTree.filter(u => u.parentId === nodeId);
      const childDisplayRows: _BR[] = [];
      for (const child of children) {
        const childFightPct = child.fightPercentage ?? 0;
        const childOwn = computeNode(child.id, nodeUser.ticketPrice, childFightPct);
        if (!childOwn) continue;
        if (child.tier === UserTier.PLAYER) {
          childDisplayRows.push(childOwn);
        } else {
          childDisplayRows.push(
            _asChild(childOwn, child.ticketPrice / 100, nodeUser.ticketPrice / 100, childFightPct / 100)
          );
        }
      }
      if (childDisplayRows.length === 0) return null;
      const childrenSum = childDisplayRows.reduce(_a, _e());
      return _agentSum(childrenSum);
    };

    const isCompany = user.tier === UserTier.COMPANY || user.tier === UserTier.MASTER;
    // viewerTP ×100 to match usersForTree scale
    const viewerTPx100 = (user.ticketPrice ?? 1) * 100;
    const viewerTPdec  = user.ticketPrice ?? 1;

    // For PLAYER: direct leaf calculation
    if (user.tier === UserTier.PLAYER) {
      const bets = betsByUser.get(user.id);
      if (!bets || bets.length === 0) return 0;
      const playerTP = (user.ticketPrice ?? 1);
      const fp = (user.fightPercentage ?? 0) / 100;
      // parent TP unknown for player self-view, use own TP as fallback
      const combined = bets.reduce((acc, b) => {
        const isWin = b.status === 'WON';
        const wl = isWin ? b.amount * (b.oddsAtTime || 1) : 0;
        return _a(acc, _leaf(b.amount, wl, playerTP, playerTP, fp));
      }, _e());
      return combined.totalC;
    }

    // For COMPANY/AGENT: Grand Total = sum of each direct child's displayRow.totalC
    // This exactly mirrors the Grand Total row calculation in WalletBalance.tsx
    const directChildren = usersForTree.filter(u => u.parentId === user.id);
    let grandTotal = 0;
    for (const child of directChildren) {
      const childFightPct = child.fightPercentage ?? 0;
      const childOwn = computeNode(child.id, viewerTPx100, childFightPct);
      if (!childOwn) continue;
      // Both Company and Agent views use agentAsChildRow.totalC as their grand total
      // (same as WalletBalance.tsx Grand Total loop: result.parentViewRow = agentAsChildRow(ownSummary,...))
      const row = _asChild(childOwn, child.ticketPrice / 100, viewerTPdec, childFightPct / 100);
      grandTotal += row.totalC;
    }
    return grandTotal;
  }, [user, allUsers, allBets, allMatches]);


  // Build navigation using translated names
  const baseNavigation = [
    { name: t.nav.matchBetting, href: ROUTE_PATHS.MATCHES, icon: Trophy },
    { name: t.nav.betHistory, href: ROUTE_PATHS.REPORTS, icon: FileText },
    { name: t.nav.afterMatchResults, href: ROUTE_PATHS.WALLET, icon: Wallet },
  ];

  const userSystemNavigation = [
    { name: t.nav.userSystem, href: ROUTE_PATHS.USERS, icon: Users },
  ];

  const companyNavigation = [
    { name: t.nav.matchManagement, href: ROUTE_PATHS.MATCH_MANAGEMENT, icon: Settings },
  ];

  const preMatchNavigation = [
    { name: t.nav.preMatchSummary, href: ROUTE_PATHS.PRE_MATCH_SUMMARY, icon: BarChart2 },
  ];

  const focusPred2Navigation = [
    { name: t.nav.focusPrediction2, href: ROUTE_PATHS.PRE_MATCH_SUMMARY_2, icon: TrendingUp },
  ];

  const navigation = (user?.tier === UserTier.MASTER || user?.tier === UserTier.COMPANY)
    ? [...userSystemNavigation, ...baseNavigation, ...preMatchNavigation, ...focusPred2Navigation, ...companyNavigation]
    : user?.tier === UserTier.AGENT
    ? [...userSystemNavigation, ...baseNavigation, ...preMatchNavigation, ...focusPred2Navigation]
    : baseNavigation;

  const getTierBadge = (tier: UserTier | undefined) => {
    switch (tier) {
      case UserTier.MASTER:
        return <Badge className="bg-purple-500/10 text-purple-700 border-purple-500/30 hover:bg-purple-500/20">{t.tier.master}</Badge>;
      case UserTier.COMPANY:
        return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20">{t.tier.company}</Badge>;
      case UserTier.AGENT:
        return <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20 hover:bg-slate-500/20">{t.tier.agent}</Badge>;
      case UserTier.PLAYER:
        return <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">{t.tier.player}</Badge>;
      default:
        return null;
    }
  };

  const getStatusIndicator = (status: UserStatus | null) => {
    switch (status) {
      case UserStatus.SUSPENDED:
        return <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/5">{t.status.suspended}</Badge>;
      case UserStatus.BANNED:
        return <Badge variant="destructive">{t.status.banned}</Badge>;
      default:
        return <Badge variant="outline" className="text-emerald-600 border-emerald-500/30 bg-emerald-500/5">{t.status.active}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background flex relative">
      {/* Football Background */}
      <div
        className="fixed inset-0 z-0 opacity-[0.03] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${IMAGES.FOOTBALL_BG_4})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(100%) contrast(1.2)',
        }}
      />

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col fixed inset-y-0 z-50 border-r bg-card/95 backdrop-blur-sm shadow-sm">
        <div className="p-6 flex items-center gap-3">
          <div />
        </div>

        <div className="px-4 py-2">
          {/* Backup button — Company/Master only */}
          {(user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER) && (
            <div className="mb-3">
              <BackupButton
                currentUser={user}
                allUsers={allUsers}
                allBets={allBets}
                allMatches={allMatches}
                fightSettings={fightSettings}
              />
            </div>
          )}
          <div className="bg-muted/30 rounded-2xl p-4 border border-border/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-border">
                <UserIcon className="w-5 h-5 text-accent-foreground" />
              </div>
              <div className="flex-1 overflow-hidden">
                {/* Nickname row with edit button + cloud prompt */}
                <div className="relative flex items-center gap-1">
                  <p className="text-sm font-semibold truncate">{user?.nickname || user?.username}</p>
                  <button
                    onClick={() => { setNicknameInput(user?.nickname || ''); setShowNicknameDialog(true); }}
                    className="flex-shrink-0 p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    title={t.userMgmt.setNickname}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  {/* Cloud bubble: only shown when no nickname is set */}
                  {!user?.nickname && (
                    <div className="absolute left-0 top-6 z-50 pointer-events-none">
                      <div className="relative bg-primary text-primary-foreground text-xs rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-[200px] leading-snug">
                        {t.userMgmt.setNicknamePrompt}
                        {/* Arrow pointing up toward the edit button */}
                        <div className="absolute -top-1.5 left-6 w-3 h-3 bg-primary rotate-45 rounded-sm" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-1">{getTierBadge(user?.tier)}</div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground">{t.nav.credit}</span>
                <span className="font-mono text-xs text-right">
                  {user?.tier === UserTier.PLAYER
                    ? formatCurrency(user?.availableCreditLimit ?? user?.creditLimit ?? 0)
                    : formatCurrency(balanceOfCreditLimit)}
                </span>
              </div>
              {/* Change Password button — players only (desktop) */}
              {user?.tier === UserTier.PLAYER && (
                <button
                  onClick={() => { setChangePwCurrent(''); setChangePwNew(''); setChangePwConfirm(''); setChangePwError(''); setChangePwSuccess(false); setShowChangePwDialog(true); }}
                  className="mt-1 w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border/60 hover:border-border rounded-lg py-1.5 transition-colors"
                >
                  <KeyRound className="w-3 h-3" />
                  Change Password
                </button>
              )}
            </div>
          </div>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          {navigation.map(item => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <item.icon className={cn('w-5 h-5 transition-transform', isActive ? 'scale-110' : 'group-hover:scale-110')} />
                <span className="font-medium">{item.name}</span>
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active"
                    className="absolute right-2"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </motion.div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Sign out — pinned to bottom of sidebar */}
        <div className="px-4 py-3 border-t mt-auto">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl"
            onClick={logout}
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{t.nav.signOut}</span>
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col lg:pl-72 min-w-0 relative z-10">
        {/* Floating mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden fixed top-3 left-1 z-50 h-10 w-10 bg-orange-500/60 hover:bg-orange-500/80 text-white border border-orange-400/50 shadow-md rounded-full"
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Menu className="w-4 h-4" />
        </Button>

        {isSuspended && (
          <div className="lg:hidden fixed top-3 left-16 z-50 flex items-center gap-2 px-3 py-1 bg-destructive/10 text-destructive border border-destructive/20 rounded-full backdrop-blur-md">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-medium">{t.nav.reportsOnly}</span>
          </div>
        )}

        <div className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[60] lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-72 bg-card z-[70] shadow-2xl lg:hidden flex flex-col"
            >
              <div className="p-6 flex items-center justify-between border-b">
                <div className="flex items-center gap-3">
                  <Trophy className="text-primary w-6 h-6" />
                  <span className="text-xl font-bold">BETPRO</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)}>
                  <X className="w-6 h-6" />
                </Button>
              </div>

              <div className="px-4 py-2">
                {/* Backup button — Company/Master only (mobile) */}
                {(user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER) && (
                  <div className="mb-3">
                    <BackupButton
                      currentUser={user}
                      allUsers={allUsers}
                      allBets={allBets}
                      allMatches={allMatches}
                      fightSettings={fightSettings}
                    />
                  </div>
                )}
                <div className="bg-muted/30 rounded-2xl p-4 border border-border/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-border">
                      <UserIcon className="w-5 h-5 text-accent-foreground" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      {/* Nickname row with edit button + cloud prompt */}
                      <div className="relative flex items-center gap-1">
                        <p className="text-sm font-semibold truncate">{user?.nickname || user?.username}</p>
                        <button
                          onClick={() => { setNicknameInput(user?.nickname || ''); setShowNicknameDialog(true); }}
                          className="flex-shrink-0 p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title={t.userMgmt.setNickname}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        {/* Cloud bubble: only shown when no nickname is set */}
                        {!user?.nickname && (
                          <div className="absolute left-0 top-6 z-50 pointer-events-none">
                            <div className="relative bg-primary text-primary-foreground text-xs rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-[200px] leading-snug">
                              {t.userMgmt.setNicknamePrompt}
                              <div className="absolute -top-1.5 left-6 w-3 h-3 bg-primary rotate-45 rounded-sm" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-1">{getTierBadge(user?.tier)}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-muted-foreground">{t.nav.credit}</span>
                      <span className="font-mono text-xs text-right">
                        {user?.tier === UserTier.PLAYER
                          ? formatCurrency((user?.creditLimit || 0) - usedCredit)
                          : formatCurrency(balanceOfCreditLimit)}
                      </span>
                    </div>
                    {/* Change Password button — players only (mobile) */}
                    {user?.tier === UserTier.PLAYER && (
                      <button
                        onClick={() => { setChangePwCurrent(''); setChangePwNew(''); setChangePwConfirm(''); setChangePwError(''); setChangePwSuccess(false); setShowChangePwDialog(true); }}
                        className="mt-1 w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border/60 hover:border-border rounded-lg py-1.5 transition-colors"
                      >
                        <KeyRound className="w-3 h-3" />
                        Change Password
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
                {navigation.map(item => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
                        isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      )
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </NavLink>
                ))}
              </nav>

              {/* Sign out — pinned to bottom of mobile sidebar */}
              <div className="px-4 py-3 border-t mt-auto">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl"
                  onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">{t.nav.signOut}</span>
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Global Copyright */}
      <div className="fixed bottom-4 right-4 text-[10px] text-muted-foreground/50 hidden md:block">
        {t.common.allRightsReserved}
      </div>

      {/* ── First-time Login Full-Screen Setup ── */}
      {showFirstLoginPopup && (
        <div className="fixed inset-0 z-[300] bg-background flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-md py-10">
            {/* Header */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Welcome! Set Up Your Account</h2>
              <p className="text-muted-foreground mt-1 text-sm">Please set your nickname to continue.</p>
            </div>

            <div className="bg-card rounded-2xl shadow-xl border border-border p-8 space-y-5">
              {/* Set Nickname */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium">
                  Set Nickname <span className="text-destructive">*</span>
                </label>
                <input
                  autoFocus
                  type="text"
                  maxLength={30}
                  value={firstLoginNickname}
                  onChange={e => { setFirstLoginNickname(e.target.value); setFirstLoginError(''); }}
                  placeholder="Enter your display name…"
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Current Password */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium">
                  Current Password <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    type={firstLoginShowOldPw ? 'text' : 'password'}
                    value={firstLoginOldPw}
                    onChange={e => { setFirstLoginOldPw(e.target.value); setFirstLoginError(''); }}
                    placeholder="Enter your current password…"
                    className="w-full h-10 rounded-lg border border-input bg-background px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setFirstLoginShowOldPw(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {firstLoginShowOldPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium">
                  New Password <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    type={firstLoginShowNewPw ? 'text' : 'password'}
                    value={firstLoginNewPw}
                    onChange={e => { setFirstLoginNewPw(e.target.value); setFirstLoginError(''); }}
                    placeholder="Enter new password…"
                    className="w-full h-10 rounded-lg border border-input bg-background px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setFirstLoginShowNewPw(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {firstLoginShowNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Minimum 4 characters.</p>
              </div>

              {/* Re-Enter New Password */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium">
                  Re-Enter New Password <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    type={firstLoginShowConfirmPw ? 'text' : 'password'}
                    value={firstLoginConfirmPw}
                    onChange={e => { setFirstLoginConfirmPw(e.target.value); setFirstLoginError(''); }}
                    placeholder="Re-enter new password…"
                    className={`w-full h-10 rounded-lg border bg-background px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
                      firstLoginConfirmPw && firstLoginNewPw !== firstLoginConfirmPw
                        ? 'border-destructive focus:ring-destructive'
                        : firstLoginConfirmPw && firstLoginNewPw === firstLoginConfirmPw
                        ? 'border-green-500 focus:ring-green-500'
                        : 'border-input'
                    }`}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setFirstLoginShowConfirmPw(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {firstLoginShowConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Live match indicator */}
                {firstLoginConfirmPw && (
                  <p className={`text-xs ${firstLoginNewPw === firstLoginConfirmPw ? 'text-green-600' : 'text-destructive'}`}>
                    {firstLoginNewPw === firstLoginConfirmPw ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Error message */}
              {firstLoginError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                  {firstLoginError}
                </div>
              )}

              {/* Save button */}
              <button
                onClick={handleFirstLoginSave}
                disabled={firstLoginSaving}
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {firstLoginSaving ? 'Saving…' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password Dialog (player only) ── */}
      {showChangePwDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowChangePwDialog(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-xs p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Change Password</h3>
            </div>

            {changePwSuccess ? (
              <p className="text-sm text-green-600 text-center py-4">Password changed successfully!</p>
            ) : (
              <div className="space-y-3">
                {/* Current Password */}
                <div className="space-y-1">
                  <label className="text-xs font-medium">Current Password <span className="text-destructive">*</span></label>
                  <div className="relative">
                    <input
                      autoFocus
                      type={changePwShowCurrent ? 'text' : 'password'}
                      value={changePwCurrent}
                      onChange={e => { setChangePwCurrent(e.target.value); setChangePwError(''); }}
                      placeholder="Enter current password"
                      className="w-full h-9 rounded-lg border border-input bg-background px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button type="button" tabIndex={-1} onClick={() => setChangePwShowCurrent(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {changePwShowCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-1">
                  <label className="text-xs font-medium">New Password <span className="text-destructive">*</span></label>
                  <div className="relative">
                    <input
                      type={changePwShowNew ? 'text' : 'password'}
                      value={changePwNew}
                      onChange={e => { setChangePwNew(e.target.value); setChangePwError(''); }}
                      placeholder="Enter new password"
                      className="w-full h-9 rounded-lg border border-input bg-background px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button type="button" tabIndex={-1} onClick={() => setChangePwShowNew(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {changePwShowNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Re-Enter New Password */}
                <div className="space-y-1">
                  <label className="text-xs font-medium">Re-Enter New Password <span className="text-destructive">*</span></label>
                  <div className="relative">
                    <input
                      type={changePwShowConfirm ? 'text' : 'password'}
                      value={changePwConfirm}
                      onChange={e => { setChangePwConfirm(e.target.value); setChangePwError(''); }}
                      placeholder="Re-enter new password"
                      className="w-full h-9 rounded-lg border border-input bg-background px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button type="button" tabIndex={-1} onClick={() => setChangePwShowConfirm(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {changePwShowConfirm ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Password match indicator */}
                {changePwNew && changePwConfirm && (
                  <p className={`text-xs ${changePwNew === changePwConfirm ? 'text-green-600' : 'text-destructive'}`}>
                    {changePwNew === changePwConfirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}

                {/* Error */}
                {changePwError && (
                  <p className="text-xs text-destructive">{changePwError}</p>
                )}

                <div className="flex gap-2 justify-end pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setShowChangePwDialog(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" disabled={changePwSaving} onClick={handleChangePassword}>
                    {changePwSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Nickname Edit Dialog ── */}
      {showNicknameDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowNicknameDialog(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-xs p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-sm mb-1">{t.userMgmt.setNickname}</h3>
            <p className="text-xs text-muted-foreground mb-4">{t.userMgmt.nicknameReceipt}</p>
            <input
              autoFocus
              type="text"
              maxLength={30}
              value={nicknameInput}
              onChange={e => setNicknameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveNickname(); if (e.key === 'Escape') setShowNicknameDialog(false); }}
              placeholder={t.userMgmt.nicknameOptional}
              className="w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-4"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowNicknameDialog(false)}>
                {t.reports.cancel}
              </Button>
              <Button size="sm" disabled={!nicknameInput.trim() || nicknameSaving} onClick={handleSaveNickname}>
                {nicknameSaving ? '...' : t.userMgmt.setNickname}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}